import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faSpinner,
  faUserCircle,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { baseURL, baseURLnoapi } from '../../Constants';
import { AuthContext } from '../../context/AuthContext';
import '../../styles/admin/admin-users-page.scss';

// Interface for Account object based on the backend model
interface Account {
  steamID: string;
  playerName: string;
  role?: string;
  team?: {
    id: string;
    name: string;
  } | null;
  isAdmin?: boolean;
  avatarUrl?: string; // Keep for backwards compatibility
}

const AdminUsersPage: React.FC = () => {
  const [users, setUsers] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState<Account[]>([]);
  const [deleteConfirmUser, setDeleteConfirmUser] = useState<Account | null>(null);
  const [deleting, setDeleting] = useState(false);
  
  // Get auth context
  const { token, isAdmin } = useContext(AuthContext);
  
  // Set page title
  useEffect(() => {
    document.title = 'User Management | Admin - Northern Championships Series';
  }, []);
  
  // Helper function to get auth headers
  const getAuthHeaders = () => {
    return {
      'Authorization': `Bearer ${token}`
    };
  };
  
  // Fetch users data
  useEffect(() => {
    if (!isAdmin) {
      setError('You do not have permission to access this page.');
      setLoading(false);
      return;
    }
    
    fetchUsers();
  }, [isAdmin]);
  
  // Filter users based on search term
  useEffect(() => {
    if (users.length > 0 && searchTerm) {
      const filtered = users.filter(user => 
        (user.playerName && user.playerName.toLowerCase().includes(searchTerm.toLowerCase())) || 
        (user.steamID && user.steamID.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.role && user.role.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.team?.name && user.team.name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchTerm, users]);
  
  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${baseURL}/Account`, {
        headers: token ? getAuthHeaders() : {}
      });
      
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Updated function that never returns null
  const getAvatarUrl = (user: Account): string => {
    if (!user || !user.steamID) {
      // Return an empty string instead of null if no user or steamID
      return '';
    }
    
    // First try the direct path to the image on our server
    return `${baseURLnoapi}/images/players/${user.steamID}.png`;
  };
  
  const handleDeleteUser = async () => {
    if (!deleteConfirmUser) return;
    
    setDeleting(true);
    
    try {
      await axios.delete(`${baseURL}/Account/${deleteConfirmUser.steamID}`, {
        headers: token ? getAuthHeaders() : {}
      });
      
      // Update the users list after successful deletion
      setUsers(prevUsers => prevUsers.filter(user => user.steamID !== deleteConfirmUser.steamID));
      setDeleteConfirmUser(null);
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Failed to delete user. Please try again later.');
    } finally {
      setDeleting(false);
    }
  };
  
  const closeDeleteConfirm = () => {
    setDeleteConfirmUser(null);
  };
  
  if (loading && users.length === 0) {
    return (
      <div className="admin-users-page loading">
        <div className="container">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="admin-users-page">
      <div className="container">
        <div className="admin-header">
          <h1>Manage Users</h1>
          <div className="header-actions">
            <Link to="/admin/users/create" className="btn-primary">
              <FontAwesomeIcon icon={faPlus} /> ADD NEW USER
            </Link>
            <div className="search-container">
              <input
                type="text"
                placeholder="Search users by name, role, or team..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              <FontAwesomeIcon icon={faSearch} className="search-icon" />
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger">
            <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
          </div>
        )}
        
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>USER</th>
                <th>STEAM ID</th>
                <th>TEAM</th>
                <th>ROLE</th>
                <th>ADMIN</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="loading-cell">
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>Loading users...</span>
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty-cell">
                    {searchTerm ? 'No users match your search.' : 'No users found.'}
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.steamID}>
                    <td>
                      <div className="user-cell">
                        {user.steamID ? (
                          <img 
                            src={getAvatarUrl(user)} 
                            alt={user.playerName} 
                            className="user-avatar"
                            onError={(e) => {
                              // First attempt failed (our server), try PowerStats
                              const target = e.target as HTMLImageElement;
                              const powerStatsUrl = `https://api.powerstats.dk/PowerStats/api/Account/Profile/${user.steamID}`;
                              
                              // Set a one-time error handler that will use the fallback icon if PowerStats also fails
                              target.onerror = () => {
                                target.onerror = null; // Prevent infinite loop
                                target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath d='M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256s256-114.6 256-256S397.4 0 256 0zM256 128c39.77 0 72 32.23 72 72S295.8 272 256 272c-39.77 0-72-32.23-72-72S216.2 128 256 128zM256 448c-52.93 0-100.9-21.53-135.7-56.29C136.5 349.9 176.5 320 224 320h64c47.54 0 87.54 29.88 103.7 71.71C356.9 426.5 308.9 448 256 448z'/%3E%3C/svg%3E";
                              };
                              
                              // Try PowerStats URL
                              target.src = powerStatsUrl;
                            }}
                          />
                        ) : (
                          <FontAwesomeIcon icon={faUserCircle} className="user-icon" />
                        )}
                        <span>{user.playerName || 'Unnamed User'}</span>
                      </div>
                    </td>
                    <td>{user.steamID}</td>
                    <td>{user.team?.name || 'No Team'}</td>
                    <td>{user.role || 'No Role'}</td>
                    <td>{user.isAdmin ? 'Admin' : ''}</td>
                    <td>
                      <div className="actions">
                        <Link 
                          to={`/admin/users/edit/${user.steamID}`} 
                          className="action-link edit"
                          title="Edit user"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Link>
                        <button
                          className="action-link delete"
                          title="Delete user"
                          onClick={() => setDeleteConfirmUser(user)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Delete Confirmation Modal */}
      {deleteConfirmUser && (
        <div className="modal-overlay" onClick={closeDeleteConfirm} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') closeDeleteConfirm(); }}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()} role="dialog" onKeyDown={(e) => e.stopPropagation()}>
            <h3>Delete User</h3>
            <p>
              Are you sure you want to delete <strong>{deleteConfirmUser.playerName || 'this user'}</strong>?
              <br />
              This action cannot be undone.
            </p>
            <div className="modal-actions">
              <button 
                className="btn-cancel"
                onClick={closeDeleteConfirm}
                disabled={deleting}
              >
                Cancel
              </button>
              <button 
                className="btn-delete"
                onClick={handleDeleteUser}
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin /> Deleting...
                  </>
                ) : (
                  <>Delete User</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;